import React, { useState } from "react";
import axios from "../../utils/axiosInstance";
import TaskSubtaskDetailModal from "./TaskSubtaskDetailModal";
import HoursProgressBar from "../ui/HoursProgressBar";
import { DragHandle } from "../ui/SortableList";

/**
 * Individual subtask row with status controls, notes, and photo upload
 * Workers can update status, add notes, and upload work photos
 * 
 * @param {boolean} readOnly - If true, hides all edit actions
 * @param {boolean} compact - If true, shows minimal UI without progress bars
 */
const SubtaskRow = ({ subtask, taskIdx, subtaskIdx, bundleId, onUpdate, dragHandleProps, readOnly = false, compact = false }) => {
  const [updating, setUpdating] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const statusColors = {
    todo: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
    blocked: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    todo: 'To Do',
    in_progress: 'In Progress',
    done: 'Done',
    blocked: 'Blocked'
  };

  const handleStatusChange = async (newStatus) => {
    if (subtask.status === newStatus) return;
    
    let comment = "";
    if (newStatus === 'blocked') {
      comment = prompt("Please provide a reason for blocking this subtask:");
      if (!comment) return; // User cancelled
    }

    setUpdating(true);
    try {
      await axios.patch(
        `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}/status`,
        { toStatus: newStatus, comment }
      );
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;

    try {
      await axios.post(
        `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}/notes`,
        { note: note.trim() }
      );
      setNote("");
      setShowNoteInput(false);
      onUpdate && onUpdate();
      alert("Note added successfully!");
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Failed to add note");
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('photos', file));

      await axios.post(
        `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}/photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      onUpdate && onUpdate();
      alert("Photos uploaded successfully!");
      
      // Reset file input
      e.target.value = "";
    } catch (error) {
      console.error("Error uploading photos:", error);
      alert("Failed to upload photos");
    } finally {
      setUploading(false);
    }
  };

  // Compact mode - minimal inline display
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-1 pl-6 text-sm">
        {/* Drag Handle */}
        {dragHandleProps && (
          <DragHandle 
            listeners={dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors"
          />
        )}
        
        {/* Bullet point */}
        <span className="text-gray-400">•</span>
        
        {/* Subtask Title */}
        <span 
          className="text-gray-700 flex-1 cursor-pointer hover:text-blue-600"
          onClick={() => setShowDetailModal(true)}
        >
          {subtask.title}
        </span>
        
        {/* Hours: reported and/or estimated */}
        {(subtask.trackedHours != null && subtask.trackedHours > 0) || subtask.estimatedHours != null ? (
          <span className="text-xs text-gray-500">
            {subtask.trackedHours != null && subtask.trackedHours > 0 && `${subtask.trackedHours}h reported`}
            {subtask.estimatedHours != null && (
              <span className="text-gray-400">
                {subtask.trackedHours > 0 ? ` / ${subtask.estimatedHours}h est.` : ` ${subtask.estimatedHours}h est.`}
              </span>
            )}
          </span>
        ) : null}

        {/* Subtask Detail Modal */}
        <TaskSubtaskDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          type="subtask"
          data={subtask}
          bundleId={bundleId}
          taskIdx={taskIdx}
          subtaskIdx={subtaskIdx}
          onUpdate={onUpdate}
          readOnly={readOnly}
        />
      </div>
    );
  }

  // Full mode - original UI with progress bars
  return (
    <div className="p-4 bg-white">
      <div className="flex items-start gap-2">
        {/* Drag Handle - only shown for admins */}
        {dragHandleProps && (
          <div className="mt-1">
            <DragHandle 
              listeners={dragHandleProps}
              className="cursor-grab active:cursor-grabbing p-1 text-gray-900 hover:text-gray-700 transition-colors"
            />
          </div>
        )}
        
        {/* Subtask Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span 
              className="font-medium text-sm text-gray-900 hover:underline cursor-pointer"
              onClick={() => setShowDetailModal(true)}
            >
              {subtask.title}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusColors[subtask.status]}`}>
              {statusLabels[subtask.status]}
            </span>
          </div>

          {/* Hours: show reported vs estimated, or reported only when no estimate */}
          {(subtask.estimatedHours != null || (subtask.trackedHours != null && subtask.trackedHours > 0)) && (
            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Hours</span>
              </div>
              <HoursProgressBar 
                estimatedHours={subtask.estimatedHours} 
                reportedHours={subtask.trackedHours ?? 0} 
              />
            </div>
          )}
        </div>
      </div>

      {/* Subtask Detail Modal */}
      <TaskSubtaskDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        type="subtask"
        data={subtask}
        bundleId={bundleId}
        taskIdx={taskIdx}
        subtaskIdx={subtaskIdx}
        onUpdate={onUpdate}
        readOnly={readOnly}
      />
    </div>
  );
};

export default SubtaskRow;
